import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const empty = { data: [], isLoading: true, error: false, count: 0, totalCount: 0, currentCount: 0 };

/** Own the embedded result locally: another tab's Redux response cannot replace it. */
export function useRelatedCollection(thunk, scope) {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const key = `${companyId}:${scope}`;
  const sequence = useRef(0);
  const [result, setResult] = useState({ ...empty, key });
  useLayoutEffect(() => {
    sequence.current += 1;
    setResult({ ...empty, key });
    return () => { sequence.current += 1; };
  }, [key]);
  const load = useCallback(async (params) => {
    const request = ++sequence.current;
    setResult((previous) => ({ ...(params.append && previous.key === key ? previous : empty), key, isLoading: true, error: false }));
    try {
      const payload = await dispatch(thunk(params)).unwrap();
      if (!Array.isArray(payload?.data)) throw new Error("Invalid list response");
      if (request !== sequence.current) return false;
      setResult((previous) => {
        const merged = params.append ? [...previous.data, ...payload.data] : payload.data;
        const data = [...new Map(merged.map((item) => [item.id, item])).values()];
        const count = Number(payload.totalCount ?? payload.count ?? data.length);
        return { key, data, count, totalCount: count, currentCount: data.length, error: false, isLoading: false };
      });
      return true;
    } catch {
      if (request === sequence.current) setResult((previous) => ({ ...previous, isLoading: false, error: true }));
      return false;
    }
  }, [dispatch, thunk, key]);
  const patch = useCallback((item) => setResult((previous) => ({ ...previous, data: previous.data.map((row) => row.id === item.id ? { ...row, ...item } : row) })), []);
  return [result.key === key ? result : empty, load, patch];
}
